import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs, Tab, Form, Spinner, Alert } from 'react-bootstrap';
import { Card, CardContent } from '@mui/material';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import SmsDetailsModal from '../../modals/SmsHistory/SmsDetailsModal/SmsDetailsModal';
import WhatsappDetailsModal from '../../modals/WhatsappCard/WhatsappDetailsModal/WhatsappDetailsModal';
import smsApi from '../../api/messaging/smsApi';
import whatsAppApi from '../../api/messaging/whatsAppApi';
import { useAuthContext } from '../../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDebounce } from '../../hooks/useDebounce';

const SmsTabContent = () => {
  const { currentEvent, isAuthenticated } = useAuthContext();

  const perPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedSms, setSelectedSms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [smsData, setSmsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isFirstSearchRender = useRef(true);


  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  const fetchSmsHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage };
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await smsApi.getSmsHistory(null, params);
      const payload = response?.data ?? response ?? {};
      const page = payload?.data ?? payload;
      const items = Array.isArray(page) ? page : page?.data ?? [];
      const normalizedItems = Array.isArray(items)
        ? items.map((item) => ({
          ...item,
          muda: item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A',
          recipientName: item.name || item.recipient_name || 'N/A',
          phoneNumber: item.number || item.phone || 'N/A',
          status: item.status_response || item.delivery_status || item.status_message || 'Unknown',
          statusResponse: item.status_response || item.delivery_status || 'N/A',
          statusMessage: item.status_message || 'N/A'
        }))
        : [];

      setSmsData(normalizedItems);
      setTotalRecords(page?.total_records ?? page?.totalRecords ?? payload?.total_records ?? payload?.totalRecords ?? null);
    } catch (err) {
      console.error('Error fetching SMS history:', err);
      setError('Failed to load SMS history');
      setSmsData([]);
      setTotalRecords(null);
    } finally {
      setLoading(false);
    }
  }, [currentEvent, currentPage, debouncedSearchTerm, statusFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSmsHistory();
    }
  }, [isAuthenticated, fetchSmsHistory]);

  const handleRowClick = (row) => {
    setSelectedSms(row);
    setShowModal(true);
  };

  const columns = [
    { name: 'Muda', selector: (row) => row.muda, sortable: true, width: '150px' },
    { name: 'Recipient Name', selector: (row) => row.recipientName, sortable: true, width: '180px' },
    { name: 'Phone Number', selector: (row) => row.phoneNumber, sortable: true, width: '140px' },
    {
      name: 'Status Response',
      selector: (row) => row.status_response || row.delivery_status || 'Unknown',
      sortable: true,
      width: '130px',
      cell: (row) => (
        <span style={{
          display: 'inline-block',
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: row.status_response === 'failed' ? '#ffebee' : '#e8edfb',
          color: row.status_response === 'failed' ? '#d32f2f' : '#0066ee',
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        }}>
          {row.status_response || row.delivery_status || 'Unknown'}
        </span>
      )
    },
    {
      name: 'Status Message',
      selector: (row) => row.status_message || 'N/A',
      sortable: true,
      wrap: true,
      width: '140px',
      cell: (row) => (
        <div style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.4em',
          maxHeight: '2.8em'
        }}>
          {row.status_message || 'N/A'}
        </div>
      )
    },
    {
      name: 'Message',
      selector: (row) => row.message,
      sortable: true,
      wrap: true,
      minWidth: '250px',
      cell: (row) => (
        <div
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer',
            width: '100%'
          }}
          onClick={() => handleRowClick(row)}
          title={row.message}
        >
          {row.message ? row.message : <span style={{ color: '#aaa', fontStyle: 'italic' }}>No content</span>}
        </div>
      )
    }
  ];

  return (
    <div>
      {totalRecords != null && (
        <div className="text-center mb-3 text-muted">
          Showing {smsData.length.toLocaleString()} of {totalRecords.toLocaleString()} records
        </div>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div className="row mb-3">
        <div className="col-md-6">
          <Form.Group controlId="searchInputSms">
            <Form.Control
              type="text"
              placeholder="Search by recipient name or phone number"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              disabled={loading}
            />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group controlId="statusFilterSms">
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
              disabled={loading}
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" color="primary" />
          <p className="mt-3">Loading SMS history...</p>
        </div>
      ) : (
        <StyledDataTable
          keyField="id"
          columns={columns}
          data={smsData}
          paginationServer
          paginationTotalRows={totalRecords || 0}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={setCurrentPage}
          paginationRowsPerPageOptions={[]}
          paginationComponentOptions={{
            noRowsPerPage: true,
            rangeSeparatorText: 'ya',
            selectAllRowsItem: false
          }}
          emptyMessage="No SMS records found"
          onRowClicked={handleRowClick}
          pointerOnHover
          pageInfo={
            totalRecords > 0
              ? `Ukurasa ${currentPage} kati ya ${Math.ceil(totalRecords / perPage)} · Jumla ya rekodi: ${totalRecords}`
              : undefined
          }
        />
      )}

      <SmsDetailsModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        sms={selectedSms}
      />
    </div>
  );
};

const WhatsappTabContent = () => {
  const { events } = useAuthContext();
  const currentEvent = events?.[0];

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [smsTypeFilter, setSmsTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whatsappData, setWhatsappData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isFirstSearchRender = useRef(true);

  const WHATSAPP_STATUS_LABELS = {
    0: 'Submitted',
    1: 'Sent',
    2: 'Delivered',
    3: 'Read',
    4: 'Received',
    5: 'Failure'
  };

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearchTerm, smsTypeFilter, statusFilter]);

  const parseWhatsappMessage = (rawMessage) => {
    if (!rawMessage) return 'N/A';
    if (typeof rawMessage !== 'string') return String(rawMessage);
    try {
      const parsed = JSON.parse(rawMessage);
      if (parsed?.components && Array.isArray(parsed.components)) {
        const bodyComponent = parsed.components.find((component) => component.type === 'body');
        if (bodyComponent?.parameters && Array.isArray(bodyComponent.parameters)) {
          return bodyComponent.parameters
            .map((param) => param?.text || '')
            .filter(Boolean)
            .join(' ')
            .trim();
        }
      }
      return parsed?.text || JSON.stringify(parsed) || rawMessage;
    } catch (_) {
      return rawMessage;
    }
  };

  const fetchWhatsappChats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage };
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      if (smsTypeFilter) {
        params.type = smsTypeFilter;
      }
      if (statusFilter !== '') {
        params.status = Number(statusFilter);
      }

      const response = await whatsAppApi.getWhatsappHistory(null, params);
      const payload = response?.data ?? response ?? {};
      const page = payload?.data ?? payload;
      const items = Array.isArray(page) ? page : page?.data ?? [];

      const normalizedItems = Array.isArray(items)
        ? items.map((item, index) => {
          const statusCode = item.status != null ? Number(item.status) : null;
          const statusLabel = statusCode != null && WHATSAPP_STATUS_LABELS[statusCode] ? WHATSAPP_STATUS_LABELS[statusCode] : 'Unknown';
          const statusText = item.status_text || statusLabel;
          return {
            ...item,
            sn: (currentPage - 1) * perPage + index + 1,
            muda: item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A',
            recipientName: item.name || item.recipient_name || item.recipient || 'N/A',
            phoneNumber: item.number || item.phone || item.display_phone_number || 'N/A',
            statusCode,
            statusLabel,
            statusText: statusText != null ? String(statusText) : 'Unknown',
            sentTime: item.sent_at || item.created_at || 'N/A',
            deliveryTime: item.delivered_at || 'N/A',
            readTime: item.read_at || 'N/A',
            reply: item.response || 'N/A',
            messageText: parseWhatsappMessage(item.message)
          };
        })
        : [];

      setWhatsappData(normalizedItems);
      setTotalRecords(page?.total_records ?? page?.totalRecords ?? payload?.total_records ?? payload?.totalRecords ?? null);
    } catch (err) {
      console.error('Error fetching WhatsApp history:', err);
      setError('Failed to load WhatsApp history');
      setWhatsappData([]);
      setTotalRecords(null);
    } finally {
      setLoading(false);
    }
  }, [currentEvent, currentPage, debouncedSearchTerm, smsTypeFilter, statusFilter, perPage]);

  useEffect(() => {
    fetchWhatsappChats();
  }, [fetchWhatsappChats]);

  const handleRowClick = (row) => {
    setSelectedMessage(row);
    setShowModal(true);
  };

  const columns = [
    {
      name: 'Sn',
      selector: (row) => row.sn,
      sortable: true,
      width: '70px',
      cell: (row) => <div>{row.sn}</div>
    },
    { name: 'Recipient Name', selector: (row) => row.recipientName, sortable: true, width: '180px' },
    { name: 'Recipient Number', selector: (row) => row.phoneNumber, sortable: true, width: '150px' },
    {
      name: 'Status',
      selector: (row) => row.statusText,
      sortable: true,
      width: '150px',
      cell: (row) => {
        const lowerStatus = String(row.statusText || '').toLowerCase();
        let bgColor = '#f0f4ff';
        let color = '#1e3a8a';
        let border = '1px solid #c7d2fe';

        if (row.statusCode === 3 || lowerStatus.includes('read')) {
          bgColor = '#ecfdf5';
          color = '#047857';
          border = '1px solid #a7f3d0';
        } else if (row.statusCode === 2 || lowerStatus.includes('delivered')) {
          bgColor = '#eff6ff';
          color = '#1d4ed8';
          border = '1px solid #bfdbfe';
        } else if (row.statusCode === 4 || lowerStatus.includes('received')) {
          bgColor = '#ecfdf5';
          color = '#047857';
          border = '1px solid #a7f3d0';
        } else if (row.statusCode === 5 || lowerStatus.includes('failure') || lowerStatus.includes('fail')) {
          bgColor = '#fff1f2';
          color = '#b91c1c';
          border = '1px solid #fecaca';
        }

        return (
          <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: bgColor,
            color: color,
            border
          }}>
            {row.statusText}
          </span>
        );
      }
    },
    { name: 'Sent Time', selector: (row) => row.sentTime, sortable: true, width: '170px' },
    { name: 'Delivery Time', selector: (row) => row.deliveryTime, sortable: true, width: '170px' },
    {
      name: 'Message',
      selector: (row) => row.messageText,
      sortable: true,
      wrap: true,
      minWidth: '250px',
      cell: (row) => (
        <div
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer',
            width: '100%'
          }}
          onClick={() => handleRowClick(row)}
          title={row.messageText !== 'N/A' ? row.messageText : ''}
        >
          {row.messageText && row.messageText !== 'N/A' ? row.messageText : <span style={{ color: '#aaa', fontStyle: 'italic' }}>No content</span>}
        </div>
      )
    }
  ];

  return (
    <Card>
      <CardContent>
        <div className="row mb-3">
          <div className="col-md-4">
            <Form.Group controlId="searchInputWhatsapp">
              <Form.Control
                type="text"
                placeholder="Search by recipient name, phone or status"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                disabled={loading}
              />
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group controlId="smsTypeFilterWhatsapp">
              <Form.Control
                as="select"
                value={smsTypeFilter}
                onChange={(e) => {
                  setSmsTypeFilter(e.target.value);
                }}
                disabled={loading}
              >
                <option value="">All Types</option>
                <option value="reminder">Reminder</option>
                <option value="invitation">Invitation</option>
                <option value="thankyou">Thank You</option>
              </Form.Control>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group controlId="statusFilterWhatsapp">
              <Form.Control
                as="select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
                disabled={loading}
              >
                <option value="">All Statuses</option>
                <option value="1">Sent</option>
                <option value="2">Delivered</option>
                <option value="3">Read</option>
                <option value="5">Failed</option>
              </Form.Control>
            </Form.Group>
          </div>
        </div>

        {error && (
          <div style={{ padding: '15px', marginBottom: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner animation="border" color="primary" />
            <p style={{ marginTop: '15px', color: '#666' }}>Loading WhatsApp chats...</p>
          </div>
        ) : (
          <StyledDataTable
            keyField="id"
            columns={columns}
            data={whatsappData}
            paginationServer
            paginationTotalRows={totalRecords || 0}
            paginationPerPage={perPage}
            paginationDefaultPage={currentPage}
            onChangePage={setCurrentPage}
            paginationRowsPerPageOptions={[]}
            paginationComponentOptions={{
              noRowsPerPage: true,
              rangeSeparatorText: 'ya',
              selectAllRowsItem: false
            }}
            emptyMessage="No WhatsApp history found"
            pageInfo={
              totalRecords > 0
                ? `Ukurasa ${currentPage} kati ya ${Math.ceil(totalRecords / perPage)} · Jumla ya rekodi: ${totalRecords}`
                : undefined
            }
          />
        )}

        <WhatsappDetailsModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          message={selectedMessage}
        />
      </CardContent>
    </Card>
  );
};

const SMSHistory = () => {
  const [activeTab, setActiveTab] = useState('sms');

  return (
    <div className="container mt-4">
      <h1 className="stylish-heading text-center" style={{ color: '#4169e1', marginBottom: '30px' }}>Message History</h1>
      <Tabs
        id="message-history-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 custom-tabs"
        fill
        variant="pills"
      >
        <Tab eventKey="sms" title="SMS History">
          <div className="mt-3">
            <SmsTabContent />
          </div>
        </Tab>
        <Tab eventKey="whatsapp" title="WhatsApp History">
          <div className="mt-3">
            <WhatsappTabContent />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default SMSHistory;
